import React, { useEffect, useState } from 'react';
import { adminAPI } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog.jsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select.jsx';
import { Loader2, Trash2, ShieldCheck, ShieldOff, UserX } from 'lucide-react';
import { toast } from 'sonner';

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const { user: currentUser } = useAuth();

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const res = await adminAPI.getUsers();
      setUsers(res.data.users || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error('Impossible de charger les utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await adminAPI.updateUser(userId, { role: newRole });
      toast.success('Rôle mis à jour');
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleToggleActive = async (userId, currentlyActive) => {
    try {
      await adminAPI.updateUser(userId, { is_active: !currentlyActive });
      toast.success(currentlyActive ? 'Compte désactivé' : 'Compte réactivé');
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await adminAPI.deleteUser(deleteTarget);
      toast.success('Utilisateur supprimé');
      setDeleteTarget(null);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Gestion des utilisateurs</h2>
          <p className="text-sm text-muted-foreground">{total} utilisateur{total > 1 ? 's' : ''}</p>
        </div>
      </div>

      <Card className="bg-card border-border">
        <CardContent className="p-0">
          {/* Header */}
          <div className="grid grid-cols-12 gap-4 px-5 py-2.5 text-xs font-medium text-muted-foreground uppercase tracking-wider bg-muted/30">
            <div className="col-span-4">Email</div>
            <div className="col-span-2">Rôle</div>
            <div className="col-span-1">Statut</div>
            <div className="col-span-2">Runs</div>
            <div className="col-span-1">Date</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>

          {/* Rows */}
          <div className="divide-y divide-border">
            {users.map((u) => {
              const isSelf = u._id === currentUser?.id;
              return (
                <div key={u._id} className="grid grid-cols-12 gap-4 px-5 py-3 items-center">
                  <div className="col-span-4 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {u.email}
                    </p>
                    <p className="text-[10px] text-muted-foreground">{u._id}</p>
                  </div>
                  <div className="col-span-2">
                    {isSelf ? (
                      <Badge className="text-xs capitalize">{u.role}</Badge>
                    ) : (
                      <Select value={u.role} onValueChange={(val) => handleRoleChange(u._id, val)}>
                        <SelectTrigger className="h-7 text-xs w-24">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="user">User</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                  <div className="col-span-1">
                    <Badge variant={u.is_active ? 'default' : 'secondary'} className="text-xs">
                      {u.is_active ? 'Actif' : 'Inactif'}
                    </Badge>
                  </div>
                  <div className="col-span-2 text-sm text-muted-foreground">
                    {u.run_count || 0} run{(u.run_count || 0) > 1 ? 's' : ''}
                  </div>
                  <div className="col-span-1 text-xs text-muted-foreground">
                    {u.created_at
                      ? new Date(u.created_at).toLocaleDateString('fr-FR')
                      : '-'}
                  </div>
                  <div className="col-span-2 flex items-center justify-end gap-1">
                    {!isSelf && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleActive(u._id, u.is_active)}
                          title={u.is_active ? 'Désactiver' : 'Réactiver'}
                        >
                          {u.is_active ? (
                            <ShieldOff className="w-3.5 h-3.5 text-amber-400" />
                          ) : (
                            <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteTarget(u._id)}
                          title="Supprimer"
                        >
                          <Trash2 className="w-3.5 h-3.5 text-destructive" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Confirmation suppression */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. L'utilisateur et toutes ses données seront supprimés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
